/**
 * @fileoverview Rich wording editor for report-template sections (MOR-962)
 *
 * A minimal TipTap editor (bold / italic / underline / lists) so template
 * styling is explicit instead of inferred — an accidental edit can no longer
 * un-bold a lead-in heading. Merge fields, [directives], and adaptation-note
 * phrases are drawn as live decorations, so the chips survive any edit.
 *
 * Storage: emits HTML. Legacy plain-text wording is lifted into paragraphs on
 * open (see `plainTextToHtml`); the generation prompts accept either format.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Extension, mergeAttributes, Node as TipTapNode } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as PmNode } from "@tiptap/pm/model";
import {
  AtSign,
  Bold,
  BookOpen,
  ClipboardList,
  Database,
  Gavel,
  Italic,
  List,
  ListOrdered,
  Scale,
  Underline as UnderlineIcon,
  Wand2,
} from "lucide-react";
import { cn } from "@/utils/misc";
import {
  DATA_TAG_CHIP,
  FLEX_PHRASE_CHIP,
  SELECTED_PASSAGE_CHIP,
  TEMPLATE_TOKEN_RE,
  tokenChipClass,
  looksLikeHtml,
  plainTextToHtml,
  type PhraseMark,
} from "./wordingFormat";
import {
  REPORT_DATA_SOURCES,
  type ReportDataSource,
} from "~/convex/jobs/reporting/dataSources";
import {
  AUTO_INSERTS,
  autoInsertToken,
} from "~/convex/jobs/reporting/autoInserts";

/**
 * Code-filled inserts and the file-search directive share the @ menu with
 * the data sources (MOR-962). They insert plain tokens rather than chips:
 * `{{auto:toc}}` is rendered by the system after drafting, and
 * `[Find in the files: …]` tells the writer to search the document library.
 */
interface InsertOption {
  id: string;
  label: string;
  group: "Insert";
  insertText: string;
  /** Caret offset from the end of `insertText` after inserting. */
  caretBack?: number;
  description: string;
}
const INSERT_OPTIONS: InsertOption[] = [
  ...AUTO_INSERTS.map((d) => ({
    id: `auto:${d.kind}`,
    label: d.label,
    group: "Insert" as const,
    insertText: `${autoInsertToken(d.kind)} `,
    description: d.description,
  })),
  {
    id: "file-search",
    label: "Find in the files…",
    group: "Insert" as const,
    insertText: "[Find in the files: ]",
    caretBack: 1,
    description: "Ask the writer to search the document library",
  },
];
type MentionOption = ReportDataSource | InsertOption;
type MentionGroup = MentionOption["group"];

/**
 * Atomic inline chip for an @-tagged data source (MOR-962). Serialised as
 * `<span data-morgan-data-tag="agentId" data-label="Name">@Name</span>` — the
 * generation prompts treat the span as "draw on this source here", never as
 * literal wording.
 */
const DataTagNode = TipTapNode.create({
  name: "dataTag",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      agentId: {
        default: "",
        parseHTML: (el: HTMLElement) =>
          el.getAttribute("data-morgan-data-tag") ?? "",
        renderHTML: (attrs: { agentId: string }) => ({
          "data-morgan-data-tag": attrs.agentId,
        }),
      },
      label: {
        default: "",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-label") ?? "",
        renderHTML: (attrs: { label: string }) => ({
          "data-label": attrs.label,
        }),
      },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-morgan-data-tag]" }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { class: DATA_TAG_CHIP }),
      `@${node.attrs.label as string}`,
    ];
  },
});

const GROUP_ICONS: Record<MentionGroup, typeof Database> = {
  "Data agent": Database,
  "Voidable transactions": Scale,
  Offences: Gavel,
  Review: BookOpen,
  "Case data": ClipboardList,
  Insert: Wand2,
};
const MENTION_GROUPS = Object.keys(GROUP_ICONS) as MentionGroup[];

/** The "@query" token immediately before the caret, if any. */
function activeMention(
  editor: Editor,
): { query: string; tokenLength: number } | null {
  const { selection } = editor.state;
  if (!selection.empty) return null;
  const $from = selection.$from;
  if (!$from.parent.isTextblock) return null;
  const textBefore = $from.parent.textBetween(
    0,
    $from.parentOffset,
    "\0",
    "\0",
  );
  const m = /(?:^|[\s\0])@([^@\0]{0,60})$/.exec(textBefore);
  if (!m) return null;
  return { query: m[1], tokenLength: m[1].length + 1 };
}

/** Build chip decorations for every text node in the doc. */
function buildDecorations(doc: PmNode, marks: PhraseMark[]): DecorationSet {
  const decorations: Decoration[] = [];
  const lowered = marks.map((m) => ({
    phrase: m.phrase.toLowerCase(),
    className: m.className,
  }));
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    const text = node.text;
    for (const match of text.matchAll(TEMPLATE_TOKEN_RE)) {
      const start = match.index ?? 0;
      decorations.push(
        Decoration.inline(pos + start, pos + start + match[0].length, {
          class: tokenChipClass(match[0]),
        }),
      );
    }
    if (lowered.length > 0) {
      const lower = text.toLowerCase();
      for (const mark of lowered) {
        let from = 0;
        for (;;) {
          const at = lower.indexOf(mark.phrase, from);
          if (at === -1) break;
          decorations.push(
            Decoration.inline(pos + at, pos + at + mark.phrase.length, {
              class: mark.className,
            }),
          );
          from = at + mark.phrase.length;
        }
      }
    }
  });
  return DecorationSet.create(doc, decorations);
}

const chipDecorationsKey = new PluginKey("wordingChipDecorations");

/**
 * Decoration extension reading the current flex phrases from a ref, so notes
 * edits re-tint without rebuilding the editor. The host nudges the view with
 * an empty transaction when the phrases change.
 */
function ChipDecorations(phrasesRef: { current: PhraseMark[] }) {
  return Extension.create({
    name: "wordingChipDecorations",
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: chipDecorationsKey,
          props: {
            decorations(state) {
              return buildDecorations(state.doc, phrasesRef.current);
            },
          },
        }),
      ];
    },
  });
}

function ToolbarButton({
  editor,
  active,
  label,
  onRun,
  children,
}: {
  editor: Editor;
  active: boolean;
  label: string;
  onRun: (e: Editor) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      // mousedown + preventDefault keeps focus in the editor so the blur
      // handler doesn't close edit mode when a formatting button is clicked.
      onMouseDown={(e) => {
        e.preventDefault();
        onRun(editor);
      }}
      aria-label={label}
      title={label}
      className={cn(
        "rounded p-1",
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

export function WordingRichEditor({
  value,
  onCommit,
  flexPhrases = [],
  selectedPhrases = [],
  placeholder,
  onTextSelect,
}: {
  /** Stored wording — HTML from this editor, or legacy plain text. */
  value: string;
  /** Fired on blur-out with the editor's HTML ("" when effectively empty). */
  onCommit: (html: string) => void;
  flexPhrases?: string[];
  /** Passages tagged for the assistant — kept visibly marked while editing. */
  selectedPhrases?: string[];
  placeholder?: string;
  /** Fires with the selected text so the assistant can target a passage. */
  onTextSelect?: (text: string) => void;
}) {
  const marks: PhraseMark[] = [
    ...flexPhrases.map((phrase) => ({ phrase, className: FLEX_PHRASE_CHIP })),
    ...selectedPhrases.map((phrase) => ({
      phrase,
      className: SELECTED_PASSAGE_CHIP,
    })),
  ];
  const phrasesRef = useRef<PhraseMark[]>(marks);
  const containerRef = useRef<HTMLDivElement>(null);

  // @-mention state for tagging data sources. Kept in refs as well so the
  // (once-captured) ProseMirror keydown handler sees current values.
  const [mention, setMention] = useState<{ query: string } | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionGroup, setMentionGroup] = useState<MentionGroup | null>(null);
  const mentionRef = useRef<{
    open: boolean;
    items: MentionOption[];
    index: number;
  }>({ open: false, items: [], index: 0 });
  const insertRef = useRef<(opt: MentionOption) => void>(() => {});

  const initialContent = useMemo(
    () => (looksLikeHtml(value) ? value : plainTextToHtml(value)),
    // The editor owns the content after mount — only the opening value matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `value` intentionally omitted: this seeds the ProseMirror doc once on mount (like a defaultValue); re-deriving it whenever the caller's `value` changes would stomp in-progress edits with a stale prop echo.
    [],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: false,
      }),
      ChipDecorations(phrasesRef),
      DataTagNode,
    ],
    content: initialContent,
    autofocus: "end",
    immediatelyRender: false,
    editorProps: {
      handleKeyDown(_view, event) {
        const m = mentionRef.current;
        if (!m.open || m.items.length === 0) return false;
        if (event.key === "ArrowDown") {
          setMentionIndex((i) => (i + 1) % m.items.length);
          return true;
        }
        if (event.key === "ArrowUp") {
          setMentionIndex((i) => (i - 1 + m.items.length) % m.items.length);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          insertRef.current(m.items[m.index]);
          return true;
        }
        if (event.key === "Escape") {
          setMention(null);
          return true;
        }
        return false;
      },
    },
    onUpdate({ editor: e }) {
      setMention(activeMention(e));
      setMentionIndex(0);
    },
    onSelectionUpdate({ editor: e }) {
      setMention(activeMention(e));
      if (!onTextSelect) return;
      const { from, to } = e.state.selection;
      if (to - from < 4) return;
      const text = e.state.doc.textBetween(from, to, "\n");
      if (text.trim().length >= 4) onTextSelect(text);
    },
  });

  // Re-tint when the notes or tagged passages change: update the ref, then
  // nudge the view so the decoration plugin recomputes.
  const marksKey = JSON.stringify(marks);
  useEffect(() => {
    phrasesRef.current = marks;
    if (editor && !editor.isDestroyed) {
      editor.view.dispatch(editor.state.tr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `marks` intentionally omitted in favor of `marksKey`: `marks` is a fresh array every render, so depending on it directly would dispatch a no-op transaction (and thrash the decoration plugin) on every keystroke; `marksKey` is the stable, content-equality proxy this effect actually needs to react to.
  }, [marksKey, editor]);

  const mentionItems = useMemo<MentionOption[]>(() => {
    if (mention === null) return [];
    const q = mention.query.trim().toLowerCase();
    const all: MentionOption[] = [...INSERT_OPTIONS, ...REPORT_DATA_SOURCES];
    return all.filter(
      (o) =>
        (mentionGroup === null || o.group === mentionGroup) &&
        (!q ||
          o.label.toLowerCase().includes(q) ||
          o.group.toLowerCase().includes(q) ||
          ("description" in o && o.description.toLowerCase().includes(q))),
    );
  }, [mention, mentionGroup]);

  // Anchor the dropdown at the caret (the "@" being typed), clamped to the
  // editor's width; recomputed as the query grows.
  const contentWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [mentionPos, setMentionPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  useEffect(() => {
    if (mention === null || !editor || editor.isDestroyed) {
      setMentionPos(null);
      return;
    }
    const wrap = contentWrapRef.current;
    if (!wrap) return;
    try {
      const coords = editor.view.coordsAtPos(editor.state.selection.from);
      const rect = wrap.getBoundingClientRect();
      setMentionPos({
        top: coords.bottom - rect.top + 4,
        left: Math.max(0, Math.min(coords.left - rect.left, rect.width - 300)),
      });
    } catch {
      setMentionPos(null);
    }
  }, [mention, editor]);
  // Keep the active row visible as arrow keys move through the list.
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [mentionIndex, mentionItems.length]);

  const insertDataTag = (opt: MentionOption) => {
    if (!editor) return;
    const active = activeMention(editor);
    const to = editor.state.selection.from;
    const from = active ? to - active.tokenLength : to;
    if ("insertText" in opt) {
      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent({ type: "text", text: opt.insertText })
        .run();
      if (opt.caretBack) {
        const pos = editor.state.selection.from - opt.caretBack;
        editor.commands.setTextSelection(pos);
      }
      setMention(null);
      return;
    }
    editor
      .chain()
      .focus()
      .deleteRange({ from, to })
      .insertContent([
        { type: "dataTag", attrs: { agentId: opt.id, label: opt.label } },
        { type: "text", text: " " },
      ])
      .run();
    setMention(null);
  };
  mentionRef.current = {
    open: mention !== null && mentionItems.length > 0,
    items: mentionItems,
    index: Math.min(mentionIndex, Math.max(0, mentionItems.length - 1)),
  };
  insertRef.current = insertDataTag;

  if (!editor) return null;

  return (
    <div
      ref={containerRef}
      className="rounded-md border border-input bg-background shadow-sm focus-within:ring-1 focus-within:ring-ring"
      onBlur={(e) => {
        // Only commit when focus leaves the whole editor (toolbar included).
        if (containerRef.current?.contains(e.relatedTarget as Node | null))
          return;
        const html = editor.isEmpty ? "" : editor.getHTML();
        onCommit(html);
      }}
    >
      <div className="flex items-center gap-0.5 border-b px-2 py-1">
        <ToolbarButton
          editor={editor}
          active={editor.isActive("bold")}
          label="Bold"
          onRun={(e) => e.chain().focus().toggleBold().run()}
        >
          <Bold className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          editor={editor}
          active={editor.isActive("italic")}
          label="Italic"
          onRun={(e) => e.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          editor={editor}
          active={editor.isActive("underline")}
          label="Underline"
          onRun={(e) => e.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="size-3.5" />
        </ToolbarButton>
        <div className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          editor={editor}
          active={editor.isActive("bulletList")}
          label="Bullet list"
          onRun={(e) => e.chain().focus().toggleBulletList().run()}
        >
          <List className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          editor={editor}
          active={editor.isActive("orderedList")}
          label="Numbered list"
          onRun={(e) => e.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-3.5" />
        </ToolbarButton>
        <div className="mx-1 h-4 w-px bg-border" />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().insertContent("@").run();
          }}
          aria-label="Tag a data source"
          title="Type @ or click to tag analysed matter data at the cursor"
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium",
            mention !== null
              ? "bg-teal-200 text-teal-950 dark:bg-teal-400/30 dark:text-teal-50"
              : "bg-teal-100 text-teal-900 hover:bg-teal-200/80 dark:bg-teal-400/20 dark:text-teal-100 dark:hover:bg-teal-400/30",
          )}
        >
          <AtSign className="size-3.5" />
          Tag data source
        </button>
        <span className="ml-auto text-[10px] text-muted-foreground">
          Click away to save
        </span>
      </div>
      <div className="border-b bg-teal-50/80 px-3 py-2 text-[11px] leading-snug text-teal-950 dark:bg-teal-400/10 dark:text-teal-100">
        <p className="font-medium">How to tag data</p>
        <p className="mt-1">
          Type @ or click Tag data source (e.g.{" "}
          <span className={cn(DATA_TAG_CHIP, "text-[10px] font-medium")}>
            @Assets
          </span>
          ) to tell Morgan which analysed data to use.
        </p>
      </div>
      <div className="relative" ref={contentWrapRef}>
        {mention !== null && mentionItems.length > 0 && mentionPos && (
          <div
            className="absolute z-20 w-[300px] overflow-hidden rounded-md border bg-popover shadow-md"
            style={{ top: mentionPos.top, left: mentionPos.left }}
          >
            <div className="border-b px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {mention.query.trim()
                ? `Matching \u201c${mention.query.trim()}\u201d`
                : "Tag a data source or insert a system block \u2014 keep typing to search"}
            </div>
            <div className="flex flex-wrap gap-1 border-b px-2 py-1.5">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setMentionGroup(null);
                  setMentionIndex(0);
                }}
                className={cn(
                  "rounded-full border px-2 py-0.5 text-[10px]",
                  mentionGroup === null
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-muted",
                )}
              >
                All
              </button>
              {MENTION_GROUPS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setMentionGroup((prev) => (prev === g ? null : g));
                    setMentionIndex(0);
                  }}
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-[10px]",
                    mentionGroup === g
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  {g}
                </button>
              ))}
            </div>
            <div ref={listRef} className="max-h-56 overflow-y-auto py-1">
              {mentionItems.map((opt, i) => {
                const GroupIcon = GROUP_ICONS[opt.group];
                const active = i === mentionRef.current.index;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-active={active ? "true" : undefined}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertDataTag(opt);
                    }}
                    onMouseEnter={() => setMentionIndex(i)}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm",
                      active ? "bg-muted" : "hover:bg-muted/60",
                    )}
                  >
                    <GroupIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">
                      {opt.label}
                      {"description" in opt && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          {opt.description}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-[10px] text-muted-foreground">
                      {opt.group}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {editor.isEmpty && placeholder && (
          <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
            {placeholder}
          </div>
        )}
        <EditorContent
          editor={editor}
          className={cn(
            "text-sm",
            "[&_.ProseMirror]:min-h-[4.5rem] [&_.ProseMirror]:px-3 [&_.ProseMirror]:py-2 [&_.ProseMirror]:outline-none",
            "[&_.ProseMirror_p]:my-1 [&_.ProseMirror_p]:leading-relaxed",
            "[&_.ProseMirror_ul]:my-1 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5",
            "[&_.ProseMirror_ol]:my-1 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5",
          )}
        />
      </div>
    </div>
  );
}
